/**
 * WebKit bridge — Surface Layer (/01)
 *
 * Detects whether the SPA is running inside a Swift WKWebView host or
 * a normal browser. Provides a single `emit()` function that dispatches
 * outbound events to the host or `console.debug`s them in browser mode.
 *
 * Inbound events from Swift arrive through `window.idlBridge.onMessage`.
 * The legacy `window.__surfaceBridge.dispatchNativeIntent` hook remains
 * as a compatibility shim for older local bundles.
 *
 * No business logic lives here. This module is the wire.
 */

import type {
  WebKitOutboundEvent,
  WebKitInboundEvent,
  BridgeMode,
} from '@contracts/idl-bridge.contract';

interface SurfaceWindow extends Window {
  webkit?: {
    messageHandlers?: {
      idl?: {
        postMessage(payload: unknown): void;
      };
      surfaceBridge?: {
        postMessage(payload: unknown): void;
      };
    };
  };
  __surfaceBridge?: {
    dispatchNativeIntent(payload: WebKitInboundEvent): void;
    mode: BridgeMode;
  };
  idlBridge?: {
    onMessage(payload: WebKitInboundEvent | string): void;
    mode: BridgeMode;
  };
}

export const BRIDGE_PAYLOAD_MAX_BYTES = 64 * 1024;

let inboundHandler: ((event: WebKitInboundEvent) => void) | null = null;

function activeNativeHandler(w: SurfaceWindow):
  | { postMessage(payload: unknown): void }
  | undefined {
  return w.webkit?.messageHandlers?.idl ?? w.webkit?.messageHandlers?.surfaceBridge;
}

function isNativeHostMode(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as SurfaceWindow;
  return Boolean(activeNativeHandler(w)?.postMessage);
}

function isLoopbackAvailable(): boolean {
  // Browser dev mode and macOS dev mode both have fetch.
  // The actual loopback check happens at the first ActionFrame POST.
  return typeof fetch === 'function';
}

export function getBridgeMode(): BridgeMode {
  return {
    isNativeHostMode: isNativeHostMode(),
    isLoopbackAvailable: isLoopbackAvailable(),
  };
}

/**
 * Send an outbound event to the host. In browser mode, logs to
 * console.debug so the developer can see the would-be payload.
 */
export function emit(event: WebKitOutboundEvent): void {
  if (typeof window === 'undefined') return;
  const w = window as SurfaceWindow;
  const nativeHandler = activeNativeHandler(w);
  const payloadBytes = new TextEncoder().encode(JSON.stringify(event)).byteLength;

  if (payloadBytes > BRIDGE_PAYLOAD_MAX_BYTES) {
    console.warn(
      '[surface-bridge] payload refused',
      { payloadBytes, maxBytes: BRIDGE_PAYLOAD_MAX_BYTES, eventType: event.type }
    );
    return;
  }

  if (nativeHandler?.postMessage) {
    try {
      nativeHandler.postMessage(event);
      return;
    } catch (err) {
      // Swift handler refused. Fall through to console for visibility.
      console.warn('[surface-bridge] native handler threw', err, event);
    }
  }

  // Browser dev mode.
  // eslint-disable-next-line no-console
  console.debug('[surface-bridge] (browser)', event.type, event.payload);
}

/**
 * Register a handler for inbound NATIVE_INTENT events. Only one
 * handler is allowed; subsequent calls replace it.
 */
export function setNativeIntentHandler(
  handler: (event: WebKitInboundEvent) => void
): void {
  inboundHandler = handler;
  if (typeof window === 'undefined') return;
  const w = window as SurfaceWindow;
  const dispatchNativeIntent = (payload: WebKitInboundEvent | string): void => {
    const event = typeof payload === 'string'
      ? (JSON.parse(payload) as WebKitInboundEvent)
      : payload;
    if (inboundHandler) inboundHandler(event);
  };

  w.idlBridge = {
    onMessage: dispatchNativeIntent,
    mode: getBridgeMode(),
  };

  w.__surfaceBridge = {
    dispatchNativeIntent,
    mode: getBridgeMode(),
  };
}

/** Default no-op for tests. */
export const NOOP_INBOUND_HANDLER = (_event: WebKitInboundEvent): void => {
  /* no-op */
};
